import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, MessageCircle, MailCheck, Clock, Sparkles, Send, CheckCircle2, XCircle, RefreshCw, FileText, CreditCard, Calendar as CalIcon, Copy } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton } from "@/components/BookingShell";
import { Pill, CHANNEL_LABEL, TRIGGER_LABEL, formatDateTime } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };

// Icon + label for each system lifecycle event kind emitted by the backend.
const EVENT_META = {
    appointment_created:        { icon: CalIcon,      label: "Appointment created" },
    appointment_approved:       { icon: CheckCircle2, label: "Appointment approved" },
    appointment_cancelled:      { icon: XCircle,      label: "Appointment cancelled" },
    appointment_rescheduled:    { icon: CalIcon,      label: "Appointment rescheduled" },
    payment_link_generated:     { icon: CreditCard,   label: "Payment link generated" },
    payment_link_regenerated:   { icon: CreditCard,   label: "Payment link regenerated" },
    payment_link_email_sent:    { icon: Mail,         label: "Payment link — Email sent" },
    payment_link_whatsapp_sent: { icon: MessageCircle,label: "Payment link — WhatsApp sent" },
    payment_link_copied:        { icon: Copy,         label: "Payment link copied" },
    payment_received:           { icon: CheckCircle2, label: "Payment received" },
    forms_sent:                 { icon: FileText,     label: "Consultation forms sent" },
    forms_completed:            { icon: FileText,     label: "Consultation forms completed" },
    note:                       { icon: Sparkles,     label: "Note" },
};

/**
 * CommunicationsTimeline
 * Renders a compact history of communications for a single appointment or customer.
 * `scope` = 'appointment' | 'customer'
 */
export function CommunicationsTimeline({ scope, id, title = "Communications" }) {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);

    const load = () => {
        setError(null);
        const p = scope === "appointment"
            ? staffApi.commsAppointmentTimeline(id)
            : staffApi.commsCustomerTimeline(id);
        p.then((r) => {
            const events = Array.isArray(r.events) ? r.events : [];
            const messages = Array.isArray(r.items) ? r.items : [];
            // Merge into a single time-sorted timeline.
            const merged = [
                ...events.map((e) => ({ _type: "event", ...e, _at: e.at })),
                ...messages.map((m) => ({ _type: "message", ...m, _at: m.scheduled_at || m.created_at })),
            ].sort((a, b) => new Date(a._at || 0) - new Date(b._at || 0));
            setItems(merged);
        }).catch(() => setError("Could not load the activity history. Please retry."));
    };
    useEffect(() => { if (id) load(); /* eslint-disable-next-line */ }, [id, scope]);

    return (
        <div className="rounded-2xl glass p-5 space-y-3" data-testid={`comms-timeline-${scope}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MailCheck className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{title}</div>
                </div>
                <div className="flex flex-wrap gap-3"><button type="button" onClick={load} className="text-[11px] text-gold inline-flex items-center gap-1"><RefreshCw className="h-3 w-3"/>Refresh</button>
                <Link to="/staff/communications" className="text-[11px] text-gold hover:underline">Open Communications →</Link></div>
            </div>

            {error ? <p role="alert" className="text-sm text-amber-200">{error} <button onClick={load} className="underline">Retry</button></p> : items === null ? <Skeleton className="h-20" /> : items.length === 0 ? (
                <p className="text-[12px] text-white/50">
                    No activity recorded yet.
                </p>
            ) : (
                <ul className="space-y-2" data-testid={`comms-timeline-${scope}-list`}>
                    {items.map((it) => {
                        if (it._type === "event") {
                            const meta = EVENT_META[it.kind] || { icon: Sparkles, label: it.title || it.kind };
                            const EIcon = meta.icon;
                            return (
                                <li key={`e-${it.id}`} className="rounded-xl border hairline p-3" data-testid={`comms-timeline-event-${it.id}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="h-8 w-8 rounded-full glass flex items-center justify-center shrink-0">
                                            <EIcon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-white text-sm truncate">{it.title || meta.label}</span>
                                                <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">{it.actor}</span>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mt-1 inline-flex items-center gap-1">
                                                <Clock className="h-2.5 w-2.5" /> {formatDateTime(it.at)}
                                            </div>
                                            {it.detail && (
                                                <div className="mt-1 text-[12px] text-white/70">{it.detail}</div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        }
                        const Icon = CHANNEL_ICON[it.channel] || Mail;
                        return (
                            <li key={`m-${it.id}`} className="rounded-xl border hairline p-3" data-testid={`comms-timeline-item-${it.id}`}>
                                <div className="flex items-start gap-3">
                                    <div className="h-8 w-8 rounded-full glass flex items-center justify-center shrink-0">
                                        <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-white text-sm truncate">{it.template_name || "—"}</span>
                                            <Pill value={it.status} />
                                        </div>
                                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 flex flex-wrap gap-1.5 mt-1">
                                            <span>{TRIGGER_LABEL[it.trigger] || it.trigger}</span>
                                            <span>·</span>
                                            <span>{CHANNEL_LABEL[it.channel]}</span>
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {formatDateTime(it.scheduled_at)}</span>
                                        </div>
                                        {it.rendered?.subject && (
                                            <div className="mt-1 text-[12px] text-white/70 truncate">{it.rendered.subject}</div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
